import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { ComplianceTable } from "../components/acms/Shared";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";

function Card({ title, accent, children, testid }) {
  return (
    <div className="glass p-5 rounded-lg" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs uppercase tracking-wider text-gray-600 font-bold">{title}</h3>
        {accent && <span className="text-[10px] uppercase tracking-widest f360-gradient-text font-bold">{accent}</span>}
      </div>
      {children}
    </div>
  );
}

// Pump Trip / Auto-Manual are independent signals from the pump's primary run/stop register
// (which already covers Pump Run Detection) — only rendered if linked, mirrors LiftsModule.jsx's
// conditional extra-register badge grid.
function PumpDetectionBadges({ live }) {
  const trip = live?.extra?.pump_trip;
  const autoManual = live?.extra?.auto_manual;
  if (!trip && !autoManual) return null;
  return (
    <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
      {trip && (
        <div className="bg-gray-50 rounded-lg p-2">
          <div className="text-gray-500">Trip</div>
          <div className={`text-sm font-bold ${Number(trip.value) === 1 ? "text-red-600" : "text-emerald-600"}`}>{Number(trip.value) === 1 ? "TRIPPED" : "Normal"}</div>
        </div>
      )}
      {autoManual && (
        <div className="bg-gray-50 rounded-lg p-2">
          <div className="text-gray-500">Auto/Manual</div>
          <div className="text-sm font-bold">{Number(autoManual.value) === 1 ? "Auto" : "Manual"}</div>
        </div>
      )}
    </div>
  );
}

export default function HydrantPage() {
  const { selectedSite, sites } = useSiteContext();
  const [hist, setHist] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [siteCfg, setSiteCfg] = useState(null);
  const [pressureSensors, setPressureSensors] = useState([]);
  const [hydrantValves, setHydrantValves] = useState([]);
  const [boosterValves, setBoosterValves] = useState([]);
  const [liveValues, setLiveValues] = useState({});

  useEffect(() => {
    if (!selectedSite) return;
    const load = () => {
      api.get(`/assets`, { params: { site_id: selectedSite, asset_type: "pump" } }).then((r) => setPumps(r.data || [])).catch(() => {});
      api.get(`/site-config-full/${selectedSite}`).then((r) => setSiteCfg(r.data)).catch(() => {});
      api.get(`/assets`, { params: { asset_type: "pressure_sensor", site_id: selectedSite, system: "hydrant" } }).then((r) => setPressureSensors(r.data || [])).catch(() => {});
      api.get(`/assets`, { params: { asset_type: "valve", site_id: selectedSite, system: "hydrant" } }).then((r) => setHydrantValves(r.data || [])).catch(() => {});
      api.get(`/assets`, { params: { asset_type: "valve", site_id: selectedSite, system: "booster" } }).then((r) => setBoosterValves(r.data || [])).catch(() => {});
      api.get(`/assets/live-values`, { params: { site_id: selectedSite } }).then((r) => setLiveValues(r.data || {})).catch(() => {});
    };
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [selectedSite]);

  // 24h trend for the first gateway-linked hydrant pressure sensor — real register history,
  // same pattern as SprinklerPage.jsx (no more telemetry-simulator fallback).
  const trendSensor = pressureSensors.find((s) => s.gateway_id && s.io_point_name);
  useEffect(() => {
    if (!selectedSite || !trendSensor) { setHist([]); return; }
    let cancelled = false;
    const load = () => {
      api.get(`/v1/sites/${selectedSite}/telemetry/parameter-history`, { params: { parameter: trendSensor.io_point_name, hours: 24 } })
        .then((r) => {
          if (cancelled) return;
          setHist((r.data || []).map((d) => ({ ...d, time: new Date(d.ingested_at || d.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })));
        }).catch(() => {});
    };
    load();
    const i = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, [selectedSite, trendSensor]);

  const site = sites.find((s) => s.site_id === selectedSite);

  const hydrantPumps = pumps.filter((p) => p.system === "hydrant");
  const boosters = pumps.filter((p) => p.meta?.pump_type === "booster" || p.system === "booster");
  const nothingRegistered = !hydrantPumps.length && !boosters.length && !hydrantValves.length && !boosterValves.length && !pressureSensors.length;

  return (
    <div className="space-y-6" data-testid="hydrant-page">
      <div>
        <div className="text-xs text-gray-500">Dashboard → {site?.name} → Hydrant System</div>
        <h1 className="text-3xl md:text-4xl font-extrabold">Hydrant System</h1>
        <p className="text-sm text-gray-500 mt-1">GR Appendix b · live telemetry · auto-refresh 5s</p>
      </div>

      {/* PRESSURE SENSORS — driven by site config (high/low zone split) + Asset Registry.
          Only renders a zone if a real sensor is tagged for it; no telemetry-simulator
          fallback values. */}
      {pressureSensors.length > 0 && (() => {
        const isSplit = !!siteCfg?.modules?.high_low_zone && (siteCfg?.zone_split_floor || 0) > 0;
        const sp = siteCfg?.setpoints || {};
        const grMin = sp.gr_min_bar || 3.5;
        const hzSensor = pressureSensors.find((s) => (s.meta || {}).zone === "high_zone");
        const lzSensor = pressureSensors.find((s) => (s.meta || {}).zone === "low_zone");
        const otherSensors = pressureSensors.filter((s) => s !== hzSensor && s !== lzSensor);
        const tone = (v) => v >= 4 ? "text-emerald-300" : v >= grMin ? "text-amber-300" : "text-red-300";
        const ZonePanel = ({ label, sensor }) => {
          const live = liveValues[sensor.asset_id];
          const val = live ? Number(live.value) : null;
          return (
            <div className="glass rounded-lg p-5" data-testid={`zone-${sensor.meta?.zone || sensor.asset_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs uppercase tracking-widest text-orange-300 font-bold">{label}</div>
                  <div className="text-[10px] text-gray-500">{sensor.name} · {sensor.location_label}</div>
                </div>
                {val != null && (
                  <div className={`text-[10px] px-2 py-0.5 rounded ${val >= grMin ? "bg-emerald-500/20 text-emerald-200" : "bg-red-500/20 text-red-200"}`}>{val >= grMin ? "PASS" : "FAIL"}</div>
                )}
              </div>
              {val != null ? (
                <div className={`text-5xl font-extrabold tabular-nums ${tone(val)}`}>{val.toFixed(2)} <span className="text-xl text-gray-500">BAR</span></div>
              ) : (
                <div className="text-xs text-gray-500 uppercase tracking-wider font-bold py-4">
                  {sensor.gateway_id ? "Awaiting data" : "Not wired to live data"}
                </div>
              )}
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">GR Min</div><div className="font-bold text-gray-900">≥ {grMin} BAR</div></div>
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">Alarm Setpoints</div><div className="font-bold text-gray-900 text-[11px]">L {sensor.meta?.alarm_low_bar ?? "—"} · H {sensor.meta?.alarm_high_bar ?? "—"}</div></div>
              </div>
            </div>
          );
        };
        return (
          <section data-testid="pressure-zones-section">
            <div className="mb-3">
              <h2 className="text-xs uppercase tracking-widest text-orange-600 font-bold">{isSplit ? "Hydrant Pressure · High/Low Zones" : "Hydrant Pressure Sensors"}</h2>
            </div>
            <div className={`grid grid-cols-1 ${isSplit && hzSensor && lzSensor ? "md:grid-cols-2" : "md:grid-cols-2 xl:grid-cols-3"} gap-4`}>
              {isSplit && hzSensor && <ZonePanel label="High Zone Pressure" sensor={hzSensor} />}
              {isSplit && lzSensor && <ZonePanel label="Low Zone Pressure" sensor={lzSensor} />}
              {otherSensors.map((s) => <ZonePanel key={s.asset_id} label={s.name} sensor={s} />)}
            </div>
          </section>
        );
      })()}

      {/* BOOSTER PUMPS (TERRACE) — driven by Pump Registry. Matches both the legacy
          meta.pump_type field (pre-System-tag pumps) and the System: Booster tag, so neither
          an old nor a newly-tagged booster pump goes missing. */}
      {boosters.length > 0 && (
        <section data-testid="booster-pumps-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-widest text-orange-300 font-bold">Booster Pumps · Terrace ({boosters.length})</h2>
            <a href="/pumps" className="text-[11px] text-orange-300 hover:underline">Edit in Pump Registry →</a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {boosters.map((b) => {
              const live = liveValues[b.asset_id];
              // legacy_status (running/stopped/fault from the pre-merge Pump model) is preserved
              // in meta for display purposes; new pumps without it fall back to the Asset status.
              const displayStatus = live ? (Number(live.value) === 1 ? "running" : "stopped") : (b.meta?.legacy_status || b.status);
              const dot = displayStatus === "running" ? "bg-emerald-500" : displayStatus === "fault" ? "bg-red-500 status-pulse-red" : "bg-slate-500";
              return (
                <Card key={b.asset_id} title={b.name} testid={`booster-${b.asset_id}`}>
                  <div className="flex items-center gap-4">
                    <span className={`w-4 h-4 rounded-full ${dot} ${live && displayStatus === "running" ? "status-pulse-green" : ""}`} />
                    <div className="flex-1">
                      <div className="text-2xl font-extrabold uppercase tracking-wider">{displayStatus}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {b.location_label || "Terrace"}
                        {" · "}
                        <span className={live ? "text-emerald-500" : "text-gray-400"}>{live ? "live" : b.gateway_id ? "awaiting data" : "not linked"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                    <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">Make/Model</div><div className="text-sm font-bold">{b.make || "—"} {b.model && `· ${b.model}`}</div></div>
                    <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">Capacity</div><div className="text-sm font-bold">{b.meta?.capacity_lpm} LPM</div></div>
                    <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">Power</div><div className="text-sm font-bold">{b.meta?.kw} kW · {b.meta?.power_source}</div></div>
                    <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">Run hours</div><div className="text-sm font-bold">{(b.meta?.run_hours_total || 0).toFixed(1)}</div></div>
                  </div>
                  <PumpDetectionBadges live={live} />
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* BOOSTER VALVES — any valve tagged System: Booster in the Asset Registry */}
      {boosterValves.length > 0 && (
        <section data-testid="booster-valves-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-widest text-orange-300 font-bold">Booster Valves · Terrace ({boosterValves.length})</h2>
            <a href="/registry/valves" className="text-[11px] text-orange-300 hover:underline">Edit in Asset Registry →</a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {boosterValves.map((v) => {
              const live = liveValues[v.asset_id];
              const open = live ? Number(live.value) === 1 : null;
              return (
                <Card key={v.asset_id} title={v.name} testid={`booster-valve-${v.asset_id}`}>
                  <div className="flex items-center gap-4">
                    <span className={`w-4 h-4 rounded-full ${open === null ? "bg-slate-500" : open ? "bg-emerald-500" : "bg-slate-500"}`} />
                    <div className="flex-1">
                      <div className="text-xl font-extrabold uppercase tracking-wider">
                        {open === null ? (v.gateway_id ? "Awaiting data" : "Not linked") : open ? "Open" : "Closed"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{v.location_label || "Terrace"} · {v.meta?.type || "—"}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* HYDRANT PUMPS — any pump tagged System: Hydrant in the Asset Registry, regardless of pump_type */}
      {hydrantPumps.length > 0 && (
        <section data-testid="hydrant-pumps-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-widest text-orange-300 font-bold">Hydrant Pumps ({hydrantPumps.length})</h2>
            <a href="/pumps" className="text-[11px] text-orange-300 hover:underline">Edit in Pump Registry →</a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {hydrantPumps.map((p) => {
              const live = liveValues[p.asset_id];
              const displayStatus = live ? (Number(live.value) === 1 ? "running" : "stopped") : (p.meta?.legacy_status || p.status);
              const dot = displayStatus === "running" ? "bg-emerald-500" : displayStatus === "fault" ? "bg-red-500 status-pulse-red" : "bg-slate-500";
              return (
                <Card key={p.asset_id} title={p.name} testid={`hydrant-pump-${p.asset_id}`}>
                  <div className="flex items-center gap-4">
                    <span className={`w-4 h-4 rounded-full ${dot} ${live && displayStatus === "running" ? "status-pulse-green" : ""}`} />
                    <div className="flex-1">
                      <div className="text-2xl font-extrabold uppercase tracking-wider">{displayStatus}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {p.location_label || "—"}
                        {" · "}
                        <span className={live ? "text-emerald-500" : "text-gray-400"}>{live ? "live" : p.gateway_id ? "awaiting data" : "not linked"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                    <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">Make/Model</div><div className="text-sm font-bold">{p.make || "—"} {p.model && `· ${p.model}`}</div></div>
                    <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">Capacity</div><div className="text-sm font-bold">{p.meta?.capacity_lpm || "—"} LPM</div></div>
                    <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">Power</div><div className="text-sm font-bold">{p.meta?.kw || "—"} kW · {p.meta?.power_source || "—"}</div></div>
                    <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">Run hours</div><div className="text-sm font-bold">{(p.meta?.run_hours_total || 0).toFixed(1)}</div></div>
                  </div>
                  <PumpDetectionBadges live={live} />
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* HYDRANT VALVES — any valve tagged System: Hydrant in the Asset Registry */}
      {hydrantValves.length > 0 && (
        <section data-testid="hydrant-valves-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-widest text-orange-300 font-bold">Hydrant Valves ({hydrantValves.length})</h2>
            <a href="/registry/valves" className="text-[11px] text-orange-300 hover:underline">Edit in Asset Registry →</a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {hydrantValves.map((v) => {
              const live = liveValues[v.asset_id];
              const open = live ? Number(live.value) === 1 : null;
              return (
                <Card key={v.asset_id} title={v.name} testid={`hydrant-valve-${v.asset_id}`}>
                  <div className="flex items-center gap-4">
                    <span className={`w-4 h-4 rounded-full ${open === null ? "bg-slate-500" : open ? "bg-emerald-500" : "bg-slate-500"}`} />
                    <div className="flex-1">
                      <div className="text-xl font-extrabold uppercase tracking-wider">
                        {open === null ? (v.gateway_id ? "Awaiting data" : "Not linked") : open ? "Open" : "Closed"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{v.location_label || "—"} · {v.meta?.type || "—"}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {nothingRegistered && (
        <div className="glass rounded-lg p-10 text-center text-gray-500 text-sm">
          No hydrant pumps, pressure sensors, or valves registered yet. <a href="/pumps" className="text-orange-600 hover:text-orange-700 font-semibold">Add them in the Asset Registry</a>
        </div>
      )}

      {/* CHART — real 24h trend for the first gateway-linked hydrant pressure sensor */}
      {hist.length > 0 && (
        <div className="glass p-5 rounded-lg">
          <h3 className="font-semibold mb-3">{trendSensor?.name || "Hydrant Riser Pressure"} — Last 24 Hours</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={hist}>
              <CartesianGrid stroke="rgba(0,0,0,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="time" stroke="#94a3b8" fontSize={10} />
              <YAxis domain={[0, trendSensor?.meta?.range_bar || 16]} stroke="#94a3b8" fontSize={10} />
              <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#1f2937" }} formatter={(v) => [`${Number(v).toFixed(2)} bar`, ""]} />
              <ReferenceLine y={trendSensor?.meta?.alarm_low_bar || 3.5} stroke="#ff3366" strokeDasharray="5 5" label={{ value: `GR ${trendSensor?.meta?.alarm_low_bar || 3.5} Bar`, position: "right", fill: "#ff3366", fontSize: 10 }} />
              <Line type="monotone" dataKey="value" stroke="#ff6b35" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {(hydrantPumps.length > 0 || pressureSensors.length > 0) && (
        <ComplianceTable
          title="Section A — Hydrant System (GR Appendix b)"
          rows={[
            ...pressureSensors.map((s) => {
              const live = liveValues[s.asset_id];
              const val = live ? Number(live.value) : null;
              const grMin = siteCfg?.setpoints?.gr_min_bar || s.meta?.alarm_low_bar || 3.5;
              return {
                parameter: s.name,
                current: val != null ? `${val.toFixed(2)} Bar` : "—",
                required: `≥ ${grMin} Bar`,
                ok: val != null ? val >= grMin : false,
              };
            }),
            ...hydrantPumps.map((p) => {
              const live = liveValues[p.asset_id];
              const running = live ? Number(live.value) === 1 : null;
              const displayStatus = live ? (running ? "Running" : "Stopped") : (p.meta?.legacy_status || p.status);
              return {
                parameter: p.name,
                current: displayStatus,
                required: "Operational",
                ok: live ? true : p.status !== "fault",
              };
            }),
          ]}
        />
      )}
    </div>
  );
}
